import { Box, Typography } from '@mui/material';
import { POST_BY_IDS_API, TRENDING_HOT_API, POPULAR_API } from '../../api/endpoint';
import { Post, PostDetail } from '../../types';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import createAxiosInstance from '../../api/axiosConfig';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

const Popular = () => {
  const navigate = useNavigate();
  const [landscapes, setLandscapes] = useState<Post[]>([]);
  const [postDetails, setPostDetails] = useState<PostDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<string | null>(null);
  const isFetching = useRef(false);
  const [activeSlide, setActiveSlide] = useState(2);
  const [stopExecution, setStopExecution] = useState(false);

  const isMobile = useMediaQuery('(max-width:600px)');
  const slideWidth = isMobile ? 200 : 460;
  const slideHeight = isMobile ? 150 : 292;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollSpeed = 20;

  const fetchPopular = async (pageToken: string | null = null, count = 20) => {
    if (!stopExecution && !isFetching.current) {
      isFetching.current = true;

      try {
        const axiosInstance = createAxiosInstance(true);
        const response = await axiosInstance.get(POPULAR_API, {
          params: { page: pageToken || '', count }
        });

        if (!response?.data?.data?.length) {
          setStopExecution(true);
          return;
        }

        setLandscapes((prevLandscapes) => [...prevLandscapes, ...response.data.data]);
        setCurrentPage(response.data.page);

        const postIds = response.data.data.map((landscape: Post) => landscape.post_id);
        if (postIds.length > 0) {
          const detailsAxios = createAxiosInstance(false);
          const postDetailsResponse = await detailsAxios.get(`${POST_BY_IDS_API}${postIds.join(',')}`);
          const shuffledData = postDetailsResponse.data.data.sort(() => Math.random() - 0.5);
          setPostDetails((prevPostDetails) => [...prevPostDetails, ...shuffledData]);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setError(error.message);
        }
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    }
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const scroll = () => {
      if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
        scrollContainer.scrollLeft = 0;
      } else {
        scrollContainer.scrollLeft += 1;
      }
    };

    const interval = setInterval(scroll, scrollSpeed);

    scrollContainer.addEventListener('mouseenter', () => clearInterval(interval));
    scrollContainer.addEventListener('mouseleave', () => setInterval(scroll, scrollSpeed));

    return () => clearInterval(interval);
  }, [postDetails]);

  const handlers = useSwipeable({
    onSwipedLeft: () => next(),
    onSwipedRight: () => prev(),
    delta: 10,
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  useEffect(() => {
    fetchPopular();
  }, []);

  useEffect(() => {
    if (postDetails.length < 5 && currentPage) {
      fetchPopular(currentPage, 5 - postDetails.length);
    }
  }, [postDetails, currentPage]);

  const validPostDetails = landscapes
    .map((landscape) => postDetails.find((detail) => detail.post_id === landscape.post_id))
    .filter((detail): detail is PostDetail => detail !== undefined);

  const next = () => {
    if (activeSlide < validPostDetails.length - 1) {
      setActiveSlide(activeSlide + 1);
    } else if (activeSlide === validPostDetails.length - 1 && !stopExecution) {
      fetchPopular(currentPage);
    }
  };

  const prev = () => {
    if (activeSlide > 0) {
      setActiveSlide(activeSlide - 1);
    }
  };

  if (error) {
    return (
      <Typography variant="h6" color="error">
        Error: {error}
      </Typography>
    );
  }

  const handlePostClick = (detail: PostDetail) => {
    navigate(`/work/${detail.post_id}`);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: isMobile ? '3rem 0' : '7rem  0'
      }}>
      <Typography
        style={{
          fontSize: isMobile ? '28px' : '52px',
          fontFamily: 'Baloo2',
          fontWeight: 800,
          color: 'white',
          padding: '20px 0',
          display: 'inline-block'
        }}>
        Popular
      </Typography>
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: '30px',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          width: '100%',
          padding: '16px'
        }}>
        {postDetails.map((item, index: number) => (
          <Box
            key={index}
            sx={{
              height: isMobile ? '126px' : '420px',
              width: isMobile
                ? item.files[0].isPortrait
                  ? '110px'
                  : '180px'
                : item.files[0].isPortrait
                  ? '350px'
                  : '600px',
              border: isMobile ? '3px solid rgba(255, 255, 255, 0.8)' : '10px solid rgba(255, 255, 255, 0.8)',
              borderRadius: isMobile ? '12px' : '40px',
              flexShrink: 0
            }}>
            <button
              onClick={() => handlePostClick(item)}
              style={{
                all: 'unset',
                display: 'block',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                borderRadius: '30px'
              }}>
              <img
                src={`${process.env.REACT_APP_DEFAULT_AVATAR}/${item.user_id}/WORKS/IMAGES/medium/${item.files[0].name}`}
                alt={item.text}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: isMobile ? '9px' : '30px'
                }}
                onError={(e) => ((e.target as HTMLImageElement).src = process.env.REACT_APP_NO_IMAGE || '')}
              />
            </button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Popular;
